import {
    AdvancedFilterGroupNode,
    AdvancedFilterNode,
    resolveAdvancedFilterContextTypeForNode,
    removeAdvancedFilterNode,
} from '@/domain/advancedFilter';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { VegobjekttypeState } from '@/state';
import { UserState } from '@/state/UserState';
import * as React from 'react';
import { AdvancedFilterGroupEditor } from './AdvancedFilterGroupEditor';
import { EgenskapClauseEditor } from './EgenskapClauseEditor';
import { OverlappClauseEditor } from './OverlappClauseEditor';
import { RelasjonClauseEditor } from './RelasjonClauseEditor';

interface Props {
    root: AdvancedFilterGroupNode;
    node: AdvancedFilterNode;
    nodeDepth: number;
    egenskapstyper: Egenskapstype[];
    vegobjekttypeData: VegobjekttypeState;
    datakatalogVegobjekttyper: Vegobjekttype[];
    errorsByNodeId: Record<string, string[]>;
    onDraftChange: (draft: AdvancedFilterGroupNode | null) => void;
    user: UserState;
    disableSuggestedValueFetch?: boolean;
}

export const AdvancedFilterNodeEditor: React.FC<Props> = ({
    root,
    node,
    nodeDepth,
    egenskapstyper,
    vegobjekttypeData,
    datakatalogVegobjekttyper,
    errorsByNodeId,
    onDraftChange,
    user,
    disableSuggestedValueFetch = false,
}) => {
    const nodeErrors = errorsByNodeId[node.id] ?? [];
    const vegobjekttypeNavn =
        resolveAdvancedFilterContextTypeForNode(
            root,
            node.id,
            datakatalogVegobjekttyper,
            vegobjekttypeData.typeId
        )?.navn ?? null;
    const onRemove = React.useCallback(() => {
        onDraftChange(removeAdvancedFilterNode(root, node.id));
    }, [node.id, onDraftChange, root]);
    const nodeLevelClass = ` c-advanced-filter__node--level-${Math.min(nodeDepth, 4)}`;

    if (node.kind === 'group') {
        return (
            <AdvancedFilterGroupEditor
                root={root}
                group={node}
                groupDepth={nodeDepth}
                label="Underfilter"
                removableNodeId={node.id}
                egenskapstyper={egenskapstyper}
                vegobjekttypeData={vegobjekttypeData}
                datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                errorsByNodeId={errorsByNodeId}
                onDraftChange={onDraftChange}
                user={user}
                disableSuggestedValueFetch={disableSuggestedValueFetch}
            />
        );
    }

    const modifier =
        node.kind === 'overlapp'
            ? ' c-advanced-filter__node--overlapp'
            : node.kind === 'relasjon'
              ? ' c-advanced-filter__node--relasjon'
              : '';

    if (node.kind === 'egenskap') {
        return (
            <div
                className={`c-advanced-filter__node c-advanced-filter__node--egenskap${nodeLevelClass}`}
            >
                <button
                    type="button"
                    className="c-advanced-filter__remove-button c-advanced-filter__remove-button--egenskap"
                    onClick={onRemove}
                >
                    Fjern
                </button>
                <div className="c-advanced-filter__node-header c-advanced-filter__node-header--egenskap">
                    <div className="c-advanced-filter__node-header-main c-advanced-filter__node-header-main--egenskap">
                        <span
                            className="c-advanced-filter__node-kind c-advanced-filter__node-kind--egenskap"
                            title={`Filtrer ${vegobjekttypeNavn ?? 'vegobjekttypen'} på egenskap`}
                        >
                            <i
                                className="material-icons c-advanced-filter__node-kind-icon"
                                aria-hidden="true"
                            >
                                filter_list
                            </i>
                            Egenskap
                        </span>
                    </div>
                    <div className="c-advanced-filter__node-content c-advanced-filter__node-content--egenskap">
                        <EgenskapClauseEditor
                            node={node}
                            vegobjekttypeData={vegobjekttypeData}
                            egenskapstyper={egenskapstyper}
                            onDraftChange={onDraftChange}
                            root={root}
                            user={user}
                            disableSuggestedValueFetch={disableSuggestedValueFetch}
                        />
                    </div>
                </div>

                {nodeErrors.length > 0 && (
                    <div className="c-advanced-filter__errors">
                        {nodeErrors.map(error => (
                            <p key={error} className="c-advanced-filter__error">
                                {error}
                            </p>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`c-advanced-filter__node${modifier}${nodeLevelClass}`}>
            <button
                type="button"
                className="c-advanced-filter__remove-button"
                onClick={onRemove}
            >
                Fjern
            </button>

            <div className="c-advanced-filter__node-card">
                {node.kind === 'overlapp' && (
                    <OverlappClauseEditor
                        root={root}
                        node={node}
                        nodeDepth={nodeDepth}
                        vegobjekttypeData={vegobjekttypeData}
                        datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                        errorsByNodeId={errorsByNodeId}
                        onDraftChange={onDraftChange}
                        user={user}
                        disableSuggestedValueFetch={disableSuggestedValueFetch}
                    />
                )}

                {node.kind === 'relasjon' && (
                    <RelasjonClauseEditor
                        root={root}
                        node={node}
                        nodeDepth={nodeDepth}
                        vegobjekttypeData={vegobjekttypeData}
                        datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                        errorsByNodeId={errorsByNodeId}
                        onDraftChange={onDraftChange}
                        user={user}
                        disableSuggestedValueFetch={disableSuggestedValueFetch}
                    />
                )}
            </div>

            {nodeErrors.length > 0 && (
                <div className="c-advanced-filter__errors">
                    {nodeErrors.map(error => (
                        <p key={error} className="c-advanced-filter__error">
                            {error}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};
